import { Link } from "react-router-dom";
import { ArrowRight, Check, MessageCircle } from "lucide-react";
import { Reveal, SectionTag } from "../components/Reveal";
import SEO, { JsonLd } from "../components/SEO";
import { whatsappLink } from "../content";

const VPV_SERVICE_LD = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Virtual Plant Visit (VPV)",
  alternateName: "Virtual Factory Tour",
  provider: { "@type": "Organization", name: "The Next Design", url: "https://tnd-creative.preview.emergentagent.com" },
  areaServed: "IN",
  description:
    "A Virtual Plant Visit (VPV) is an interactive walk through your real factory that lets buyers anywhere explore your plant in full detail, as per their requirements, building trust before the first meeting.",
};

const VPV_FAQ_LD = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What is a Virtual Plant Visit (VPV)?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "VPV is a Virtual Plant Visit, an excellent solution for manufacturers who have a great factory setup. It helps you showcase your plant in full detail, as per your buyer's requirements, so any buyer anywhere can walk through your real plant before the first meeting.",
      },
    },
    {
      "@type": "Question",
      name: "Why does a VPV matter for manufacturers?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "For manufacturers, the factory is the strongest sales argument, but most buyers never see it before deciding. A VPV removes that bottleneck: buyers verify scale, process discipline and infrastructure on their own time, which shortens sales cycles and lifts lead conversion.",
      },
    },
  ],
};

export default function VPV() {
  return (
    <div data-testid="vpv-page">
      <SEO
        title="Virtual Plant Visit (VPV) for Manufacturers, Real Factories. Real Confidence. | The Next Design"
        description="A Virtual Plant Visit (VPV) lets buyers walk through your real factory from anywhere, showcasing your plant in full detail, building trust and lifting lead conversion. By The Next Design, India."
      />
      <JsonLd data={VPV_SERVICE_LD} />
      <JsonLd data={VPV_FAQ_LD} />
      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <SectionTag>VPV, Virtual Plant Visit</SectionTag>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light uppercase tracking-tight leading-[0.95]" data-testid="vpv-heading">
                Real Factories.<br /><span className="text-brand-red">Real Confidence.</span>
              </h1>
              <p className="mt-8 text-lg text-neutral-600 max-w-xl leading-relaxed">
                A Virtual Plant Visit (VPV) opens your factory to any serious buyer, anywhere in the world, a guided, interactive walk through your plant that builds trust before the first meeting is ever scheduled.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={whatsappLink("Hi Nitin, I'd like to discuss a Virtual Plant Visit (VPV) for our factory.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="vpv-hero-whatsapp-button"
                  className="bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors flex items-center gap-2"
                >
                  Discuss a VPV <MessageCircle size={16} />
                </a>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="img-zoom border border-neutral-900" data-testid="vpv-hero-image">
                <img src={`${process.env.PUBLIC_URL}/assets/vpv-dashboard-param-dairy.png`} alt="Virtual Plant Visit dashboard, Param Dairy" className="w-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal><SectionTag>What is a VPV?</SectionTag></Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight leading-tight" data-testid="vpv-what-heading">
                Your plant's credibility, available on demand.
              </h2>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-8 text-neutral-600 leading-relaxed">
                <p>
                  VPV is a Virtual Plant Visit, an excellent solution for manufacturers who have a great factory setup. It helps you showcase your plant in full detail, as per your buyer's requirements.
                </p>
                <p>
                  Buyers explore your real plant, production lines, quality labs, infrastructure, on their own time, from any device, and arrive at the first conversation already convinced of your capability. VPV is the brainchild of <span className="text-neutral-900 font-semibold">Nitin Gupta</span>, Export Marketing Strategist based out of Gurgaon.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal>
            <SectionTag>Why it matters</SectionTag>
            <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight max-w-3xl" data-testid="vpv-why-heading">
              Trust is the real conversion metric.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-200 border border-neutral-200">
            {[
              { t: "Win larger buyers", d: "Corporate and export buyers shortlist suppliers they can verify. A VPV makes verification effortless." },
              { t: "Shorten sales cycles", d: "Buyers who have walked your plant virtually skip the doubt phase and move straight to specifics." },
              { t: "Compete beyond price", d: "When buyers see process discipline and infrastructure, the conversation shifts from cost to capability." },
              { t: "Sell across geographies", d: "Your plant works for you 24/7, for buyers who will never travel to see it in person." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08} className="bg-white">
                <div className="p-8 h-full" data-testid={`vpv-benefit-${i}`}>
                  <Check className="text-brand-red" size={22} />
                  <h3 className="mt-4 font-display text-xl font-semibold tracking-tight">{c.t}</h3>
                  <p className="mt-3 text-sm text-neutral-600 leading-relaxed">{c.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <SectionTag>Inside a VPV</SectionTag>
              <h2 className="font-display text-4xl sm:text-5xl font-medium tracking-tight" data-testid="vpv-gallery-heading">
                A walk through a real plant.
              </h2>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { src: `${process.env.PUBLIC_URL}/assets/vpv-dashboard-param-dairy.png`, cap: "Param Dairy · VPV index dashboard" },
              { src: `${process.env.PUBLIC_URL}/assets/vpv-dashboard-nandan.png`, cap: "Nandan · factory index dashboard · click to explore the live tour", href: "https://kuula.co/share/5dBjd/collection/7ckpl?logo=-1&info=0&fs=1&vr=1&sd=1&autorotate=1.5&autop=10&thumbs=1" },
              { src: `${process.env.PUBLIC_URL}/assets/vpv-dashboard-saa-vishnu.jpg`, cap: "Saa Vishnu Group · multi-plant dashboard" },
            ].map((g, i) => (
              <Reveal key={g.src} delay={i * 0.06}>
                <figure className="bg-white border border-neutral-200 hover:border-neutral-950 transition-colors" data-testid={`vpv-gallery-item-${i}`}>
                  {g.href ? (
                    <a href={g.href} target="_blank" rel="noopener noreferrer" className="block group" data-testid="vpv-gallery-nandan-link">
                      <div className="p-6 flex items-center justify-center">
                        <img src={g.src} alt={g.cap} className="w-full h-auto object-contain" loading="lazy" />
                      </div>
                      <figcaption className="p-4 text-xs uppercase tracking-widest text-neutral-500 group-hover:text-brand-red transition-colors border-t border-neutral-100">{g.cap}</figcaption>
                    </a>
                  ) : (
                    <>
                      <div className="p-6 flex items-center justify-center">
                        <img src={g.src} alt={g.cap} className="w-full h-auto object-contain" loading="lazy" />
                      </div>
                      <figcaption className="p-4 text-xs uppercase tracking-widest text-neutral-500 border-t border-neutral-100">{g.cap}</figcaption>
                    </>
                  )}
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-neutral-950 text-white border-b border-neutral-900" data-testid="vpv-case-study-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <div className="aspect-video border border-neutral-700" data-testid="vpv-kesaria-video">
                <iframe
                  src="https://www.youtube.com/embed/62asCfqRRbw?start=64"
                  title="Kesaria Rubber Industries, Virtual Plant Visit"
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.1}>
              <p className="text-sm font-bold uppercase tracking-[0.25em] text-brand-red mb-6">VPV in the Field</p>
              <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight leading-tight" data-testid="vpv-kesaria-heading">
                Kesaria Rubber's buyers stopped needing to travel.
              </h2>
              <p className="mt-6 text-neutral-400 leading-relaxed">
                Kesaria Rubber Industries had a plant worth seeing, but getting buyers to visit was the bottleneck. After their Virtual Plant Visit, lead conversion is above 80%, as stated by their export head.
              </p>
              <p className="mt-8 font-display text-6xl font-semibold text-brand-red" data-testid="vpv-kesaria-stat">80%+</p>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-neutral-500">Lead conversion after VPV</p>
              <Link
                to="/case-studies/vpv-kesaria-rubber"
                data-testid="vpv-kesaria-case-study-link"
                className="mt-8 inline-flex items-center gap-2 font-bold uppercase tracking-widest text-sm text-white link-underline"
              >
                Read the Kesaria case study <ArrowRight size={16} className="text-brand-red" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-neutral-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-display text-4xl sm:text-6xl font-semibold uppercase tracking-tight leading-none" data-testid="vpv-cta-heading">
                Your factory is your best salesman.<br /><span className="text-brand-red">Put it to work.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4">
            <Reveal delay={0.1}>
              <a
                href={whatsappLink("Hi Nitin, I'd like to discuss a Virtual Plant Visit (VPV) for our factory.")}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="vpv-cta-whatsapp-button"
                className="bg-brand-red hover:bg-brand-red-dark text-white px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors flex items-center justify-center gap-2 w-full"
              >
                WhatsApp Nitin <MessageCircle size={16} />
              </a>
              <Link
                to="/contact"
                data-testid="vpv-cta-enquiry-link"
                className="mt-4 border border-white text-white hover:bg-white hover:text-neutral-950 px-8 py-4 font-bold uppercase tracking-widest text-sm transition-colors flex items-center justify-center gap-2 w-full"
              >
                Send an Enquiry <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
